import { PropertyOperation, PropertyOrientation } from 'src/api/generated';
import { propertyClassificationOptions } from './property-classification.util';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/** Currency selector option used by property price fields. */
export interface PropertyEditorCurrencyOption {
  /** Display content shown in the currency selector. */
  content: string;
  /** Currency code submitted by the form. */
  value: string;
}

const normalizeCurrencyCode = (currency?: string | null): string | null => {
  const normalized = currency?.trim().toUpperCase();

  if (!normalized) {
    return null;
  }

  return normalized;
};

/**
 * Builds property currency selector options from tenant settings and existing
 * property values.
 *
 * @param enabledCurrencies Tenant enabled currency setting values.
 * @param currentCurrencies Existing or currently selected property currencies.
 * @returns Normalized, deduplicated selector options.
 */
export const buildPropertyEditorCurrencyOptions = (
  enabledCurrencies: readonly (string | null | undefined)[],
  currentCurrencies: readonly (string | null | undefined)[] = []
): PropertyEditorCurrencyOption[] => {
  const uniqueCurrencies = new Set<string>();

  [...enabledCurrencies, ...currentCurrencies].forEach((currency) => {
    const normalized = normalizeCurrencyCode(currency);

    if (normalized) {
      uniqueCurrencies.add(normalized);
    }
  });

  return Array.from(uniqueCurrencies).map((currency) => ({
    content: currency,
    value: currency,
  }));
};

/**
 * Resolves the default currency for new empty price fields.
 *
 * @param enabledCurrencies Tenant enabled currency setting values.
 * @returns The first valid enabled currency, or an empty string.
 */
export const getDefaultPropertyCurrency = (
  enabledCurrencies: readonly (string | null | undefined)[]
): string => {
  for (const currency of enabledCurrencies) {
    const normalized = normalizeCurrencyCode(currency);

    if (normalized) {
      return normalized;
    }
  }

  return '';
};

export const propertyEditorPropertyTypeOptions =
  propertyClassificationOptions.map(({ value, labelKey }) => ({
    value,
    labelKey,
  }));

export const propertyEditorPropertyConditionOptions = [
  {
    value: 'USED',
    labelKey: 'catalog.properties.propertyCondition.USED' as const,
  },
  {
    value: 'NEW',
    labelKey: 'catalog.properties.propertyCondition.NEW' as const,
  },
] as const;

export const propertyEditorOrientationOptions = [
  { value: '', labelKey: null },
  {
    value: PropertyOrientation.N,
    labelKey: 'catalog.properties.form.orientation.N' as const,
  },
  {
    value: PropertyOrientation.S,
    labelKey: 'catalog.properties.form.orientation.S' as const,
  },
  {
    value: PropertyOrientation.E,
    labelKey: 'catalog.properties.form.orientation.E' as const,
  },
  {
    value: PropertyOrientation.W,
    labelKey: 'catalog.properties.form.orientation.W' as const,
  },
  {
    value: PropertyOrientation.NE,
    labelKey: 'catalog.properties.form.orientation.NE' as const,
  },
  {
    value: PropertyOrientation.NW,
    labelKey: 'catalog.properties.form.orientation.NW' as const,
  },
  {
    value: PropertyOrientation.SE,
    labelKey: 'catalog.properties.form.orientation.SE' as const,
  },
  {
    value: PropertyOrientation.SW,
    labelKey: 'catalog.properties.form.orientation.SW' as const,
  },
] as const;

interface PropertyFeatureFieldDefinition {
  fieldName: string;
  labelKey: TranslationKey;
}

interface PropertyFeatureGroupDefinition {
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  features: readonly PropertyFeatureFieldDefinition[];
}

export const propertyFeatureGroups: readonly PropertyFeatureGroupDefinition[] =
  [
    {
      titleKey:
        'catalog.properties.form.featureGroup.conditions.title' as const,
      descriptionKey:
        'catalog.properties.form.featureGroup.conditions.description' as const,
      features: [
        {
          fieldName: 'furnished',
          labelKey: 'catalog.properties.form.feature.furnished',
        },
        {
          fieldName: 'petsAllowed',
          labelKey: 'catalog.properties.form.feature.petsAllowed',
        },
        {
          fieldName: 'hasVirtualTour',
          labelKey: 'catalog.properties.form.feature.hasVirtualTour',
        },
      ],
    },
    {
      titleKey:
        'catalog.properties.form.featureGroup.privateSpaces.title' as const,
      descriptionKey:
        'catalog.properties.form.featureGroup.privateSpaces.description' as const,
      features: [
        {
          fieldName: 'hasBalcony',
          labelKey: 'catalog.properties.form.feature.hasBalcony',
        },
        {
          fieldName: 'hasKitchen',
          labelKey: 'catalog.properties.form.feature.hasKitchen',
        },
        {
          fieldName: 'hasGarden',
          labelKey: 'catalog.properties.form.feature.hasGarden',
        },
        {
          fieldName: 'hasPatio',
          labelKey: 'catalog.properties.form.feature.hasPatio',
        },
        {
          fieldName: 'hasTerrace',
          labelKey: 'catalog.properties.form.feature.hasTerrace',
        },
        {
          fieldName: 'hasLaundry',
          labelKey: 'catalog.properties.form.feature.hasLaundry',
        },
        {
          fieldName: 'hasHeating',
          labelKey: 'catalog.properties.form.feature.hasHeating',
        },
        {
          fieldName: 'hasAirConditioning',
          labelKey: 'catalog.properties.form.feature.hasAirConditioning',
        },
        {
          fieldName: 'hasInternetAccess',
          labelKey: 'catalog.properties.form.feature.hasInternetAccess',
        },
      ],
    },
    {
      titleKey:
        'catalog.properties.form.featureGroup.commonAreas.title' as const,
      descriptionKey:
        'catalog.properties.form.featureGroup.commonAreas.description' as const,
      features: [
        {
          fieldName: 'hasLift',
          labelKey: 'catalog.properties.form.feature.hasLift',
        },
        {
          fieldName: 'hasPlayground',
          labelKey: 'catalog.properties.form.feature.hasPlayground',
        },
        {
          fieldName: 'hasRoofGarden',
          labelKey: 'catalog.properties.form.feature.hasRoofGarden',
        },
        {
          fieldName: 'hasBusinessCenter',
          labelKey: 'catalog.properties.form.feature.hasBusinessCenter',
        },
        {
          fieldName: 'hasCinemaHall',
          labelKey: 'catalog.properties.form.feature.hasCinemaHall',
        },
        {
          fieldName: 'hasTennisCourt',
          labelKey: 'catalog.properties.form.feature.hasTennisCourt',
        },
        {
          fieldName: 'hasPool',
          labelKey: 'catalog.properties.form.feature.hasPool',
        },
        {
          fieldName: 'hasGym',
          labelKey: 'catalog.properties.form.feature.hasGym',
        },
        {
          fieldName: 'hasSecurity',
          labelKey: 'catalog.properties.form.feature.hasSecurity',
        },
      ],
    },
  ] as const;

export const propertyFeatureFieldDefinitions = propertyFeatureGroups.flatMap(
  (group) => group.features
) as readonly PropertyFeatureFieldDefinition[];

/**
 * Returns the translation key that names a property feature state.
 *
 * @param value Feature value: `true`, `false`, or `null` when it is not set.
 * @returns Key for "Yes", "No" or "Not set".
 */
export const getPropertyFeatureStateLabelKey = (
  value: boolean | null
): TranslationKey => {
  if (value === true) {
    return 'catalog.properties.form.booleanState.yes';
  }

  return value === false
    ? 'catalog.properties.form.booleanState.no'
    : 'catalog.properties.form.booleanState.unset';
};

export interface PropertySurfaceVisibility {
  /** Whether to show the usable/indoor area input. */
  showUsable: boolean;
  /** Whether to show the covered/built area input. */
  showCovered: boolean;
  /** Whether to show the total area input. */
  showTotal: boolean;
  /** Whether to show the outdoor/exterior area input. */
  showOutdoor: boolean;
}

export const getSurfaceTotalHelpKey = (
  propertyType?: string | null
): TranslationKey => {
  if (
    propertyType === 'HOUSE' ||
    propertyType === 'LAND' ||
    propertyType === 'PARCEL'
  ) {
    return 'catalog.properties.form.surfaceTotalHelpLot';
  }

  return 'catalog.properties.form.surfaceTotalHelpUnit';
};

export const getSurfaceUsableHelpKey = (
  propertyType?: string | null
): TranslationKey => {
  if (propertyType === 'LAND') {
    return 'catalog.properties.form.surfaceUsableHelpOptional';
  }

  return 'catalog.properties.form.surfaceUsableHelp';
};

export const getSurfaceCoveredHelpKey = (
  propertyType?: string | null
): TranslationKey => {
  if (propertyType === 'LAND') {
    return 'catalog.properties.form.surfaceCoveredHelpOptional';
  }

  return 'catalog.properties.form.surfaceCoveredHelp';
};

export const getSurfaceOutdoorHelpKey = (
  propertyType?: string | null
): TranslationKey => {
  if (propertyType === 'APARTMENT') {
    return 'catalog.properties.form.surfaceOutdoorHelpApartment';
  }

  if (
    propertyType === 'HOUSE' ||
    propertyType === 'LAND' ||
    propertyType === 'PARCEL'
  ) {
    return 'catalog.properties.form.surfaceOutdoorHelpLot';
  }

  return 'catalog.properties.form.surfaceOutdoorHelp';
};

export const getVisibleSurfaceFields = (
  propertyType?: string | null
): PropertySurfaceVisibility => {
  if (propertyType === 'LAND') {
    return {
      showUsable: false,
      showCovered: false,
      showTotal: true,
      showOutdoor: true,
    };
  }

  if (propertyType === 'PARKING') {
    return {
      showUsable: false,
      showCovered: false,
      showTotal: true,
      showOutdoor: false,
    };
  }

  return {
    showUsable: true,
    showCovered: true,
    showTotal: true,
    showOutdoor: true,
  };
};

export const shouldShowSalePricing = (operation?: PropertyOperation | null) =>
  operation === PropertyOperation.SALE ||
  operation === PropertyOperation.SALE_AND_RENT;

export const shouldShowRentPricing = (operation?: PropertyOperation | null) =>
  operation === PropertyOperation.RENT ||
  operation === PropertyOperation.SALE_AND_RENT ||
  operation === PropertyOperation.TEMPORARY_RENT;
